import React, { useState, useEffect } from 'react';
import notificationService, { Notification } from '../services/notificationService';

interface NotificationContainerProps {
  className?: string;
}

const NotificationIcon = ({ type, className = 'w-5 h-5' }: { type: Notification['type']; className?: string }) => {
  const iconClass = `${className} flex-shrink-0`;
  switch (type) {
    case 'success':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'error':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'warning':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    case 'info':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'notice':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      );
  }
};

const NotificationContainer: React.FC<NotificationContainerProps> = ({ className = '' }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const handleNotificationAdded = (notification: Notification) => {
      setNotifications(prev => [notification, ...prev]);

      if (notification.duration && notification.duration > 0) {
        setTimeout(() => {
          notificationService.removeNotification(notification.id);
        }, notification.duration);
      }
    };

    const handleNotificationRemoved = (id: string) => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const handleNotificationsCleared = () => {
      setNotifications([]);
    };

    notificationService.on('notificationAdded', handleNotificationAdded);
    notificationService.on('notificationRemoved', handleNotificationRemoved);
    notificationService.on('notificationsCleared', handleNotificationsCleared);

    setNotifications(notificationService.getNotifications());

    return () => {
      notificationService.off('notificationAdded', handleNotificationAdded);
      notificationService.off('notificationRemoved', handleNotificationRemoved);
      notificationService.off('notificationsCleared', handleNotificationsCleared);
    };
  }, []);

  const handleRemoveNotification = (id: string) => {
    notificationService.removeNotification(id);
  };

  const getNotificationStyles = (type: Notification['type']) => {
    const styles = {
      success: {
        border: 'border-l-emerald-500',
        iconBg: 'bg-emerald-500/15',
        iconColor: 'text-emerald-400',
      },
      error: {
        border: 'border-l-red-500',
        iconBg: 'bg-red-500/15',
        iconColor: 'text-red-400',
      },
      warning: {
        border: 'border-l-amber-500',
        iconBg: 'bg-amber-500/15',
        iconColor: 'text-amber-400',
      },
      info: {
        border: 'border-l-blue-500',
        iconBg: 'bg-blue-500/15',
        iconColor: 'text-blue-400',
      },
      notice: {
        border: 'border-l-violet-500',
        iconBg: 'bg-violet-500/15',
        iconColor: 'text-violet-400',
      },
    };
    return styles[type] || styles.info;
  };

  const formatTime = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return timestamp.toLocaleDateString();
  };

  return (
    <div
      className={`fixed top-4 right-4 z-[9999] space-y-3 max-w-md w-[calc(100vw-2rem)] sm:w-96 ${className}`}
      role="region"
      aria-label="Notifications"
    >
      {notifications.map((notification) => {
        const styles = getNotificationStyles(notification.type);
        return (
          <div
            key={notification.id}
            className={`
              bg-dark-800/95 backdrop-blur-xl border border-dark-600/50 rounded-xl shadow-card
              border-l-4 ${styles.border}
              p-4 transition-all duration-300 ease-out
              animate-fadeIn
              hover:shadow-card-hover
            `}
          >
            <div className="flex gap-3">
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${styles.iconBg} ${styles.iconColor}`}
              >
                <NotificationIcon type={notification.type} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-semibold text-white leading-tight">
                    {notification.title}
                  </h4>
                  <button
                    onClick={() => handleRemoveNotification(notification.id)}
                    className="flex-shrink-0 p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors duration-150"
                    aria-label="Dismiss notification"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <p className="text-sm text-gray-300 mt-1 leading-relaxed">
                  {notification.message}
                </p>

                <div className="text-xs text-gray-500 mt-2 font-medium">
                  {formatTime(notification.timestamp)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NotificationContainer;
